import { produce } from 'immer'
import { isEmpty } from 'lodash'

// Action Types
export const LOAD_NOTIFICATION = 'notifications/loadNotification'
export const SET_NOTIFICATIONS = 'notifications/setNotifications'
export const SET_UNREAD_COUNT = 'notifications/setUnreadCount'
export const MARK_READ = 'notifications/markRead'
export const MARK_ALL_READ = 'notifications/markAllRead'
// Undo an optimistic MARK_ALL_READ when its server PATCH fails. Carries the
// ids that were unread at click time so only those rows are re-marked.
export const RESTORE_UNREAD = 'notifications/restoreUnread'
export const DISMISS_NOTIFICATION = 'notifications/dismiss'
// Server-side revocation push (raised-hand lowered).
export const CLEAR_NOTIFICATIONS = 'notifications/clearNotifications'
export const TOGGLE_PANEL = 'notifications/togglePanel'
// Explicit open/close (vs TOGGLE_PANEL) — used when the intent is
// unambiguous, e.g. the collapsed-sidebar bell, which must always OPEN
// the panel (after expanding the sidebar), never toggle it shut.
export const SET_PANEL_OPEN = 'notifications/setPanelOpen'
export const SET_LOADING = 'notifications/setLoading'
// currentOrg* hold the current user's own-org users + tags for the
// settings modal's agent/tag filter dropdowns. Kept separate from the
// shared organizationUsers/organizationTags slices so a sibling page
// viewing a different org (e.g., a Balto admin on /organizations/5)
// can't surface the wrong org's data in the preferences modal.
export const SET_CURRENT_ORG_USERS = 'notifications/setCurrentOrgUsers'
export const SET_CURRENT_ORG_TAGS = 'notifications/setCurrentOrgTags'
// The current manager's RTA alert-config coverage, aggregated across their
// configs: { allUsers, userIds, hasTagConfigs }. Drives the raised_hand "set up
// an alert for this agent" guidance — we nudge a manager who doesn't cover the
// agent instead of offering Listen/Chat they can't use.
export const SET_ALERT_COVERAGE = 'notifications/setAlertCoverage'

// Initial State
export const initialState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  panelOpen: false,
  currentOrgUsers: [],
  currentOrgTags: [],
  // null = not yet fetched (don't show guidance while unknown); set to
  // { allUsers, userIds, hasTagConfigs } once fetched on first panel open.
  alertCoverage: null,
}

// Reducer
export default function notifications(state = initialState, action) {
  return produce(state, (newState) => {
    switch (action.type) {
      case LOAD_NOTIFICATION: {
        // Idempotent on id — the same notification can arrive twice when both
        // the RTC socket and the dedicated notification socket are open
        // against the same redis channel. Don't double-count.
        const incoming = action.notification
        const alreadyPresent = state.notifications.some(
          (notification) => notification.id === incoming.id
        )
        if (alreadyPresent) {
          return
        }
        newState.notifications = [incoming, ...state.notifications]
        newState.unreadCount = state.unreadCount + 1
        break
      }
      case SET_NOTIFICATIONS: {
        // unreadCount stays server-authoritative via SET_UNREAD_COUNT —
        // recomputing here would collapse the badge for users with more
        // unread items than fit in the paginated response (e.g. >20).
        // Callers that need to refresh the count should dispatch
        // fetchUnreadCount alongside fetchNotifications.
        //
        // `metadata` is ride-along WS-only context (never persisted —
        // see notification-service), so the fetched rows don't carry it.
        // Preserve it from the matching in-memory notification so a refetch
        // (e.g. opening the panel) doesn't strip the live affordances —
        // raised_hand Listen/Chat — that a WS push delivered this session.
        const metadataById = new Map(
          state.notifications
            .filter((notification) => !isEmpty(notification.metadata))
            .map((notification) => [notification.id, notification.metadata])
        )
        newState.notifications = action.notifications.map((notification) =>
          metadataById.has(notification.id)
            ? { ...notification, metadata: metadataById.get(notification.id) }
            : notification
        )
        newState.loading = false
        break
      }
      case SET_UNREAD_COUNT:
        newState.unreadCount = action.count
        break
      case MARK_READ: {
        // Only decrement unreadCount when the matched row was actually
        // unread — defends against a stray MARK_READ against an already-
        // read row underflowing the badge. The clamp at zero already
        // prevents negatives, but it doesn't restore the correct count.
        const target = newState.notifications.find(
          (notification) => notification.id === action.notificationId
        )
        if (target && !target.read) {
          target.read = true
          newState.unreadCount = Math.max(0, state.unreadCount - 1)
        } else if (target) {
          target.read = true
        }
        break
      }
      case MARK_ALL_READ:
        newState.notifications.forEach((notification) => {
          notification.read = true
        })
        newState.unreadCount = 0
        break
      case RESTORE_UNREAD: {
        // Rolls back a failed optimistic MARK_ALL_READ. Re-marks only the rows
        // that were unread at click time, walking the CURRENT list — so a
        // notification that arrived via WebSocket during the in-flight PATCH is
        // preserved rather than clobbered by a stale click-time snapshot, and
        // the badge counts up per genuinely-restored row.
        action.notificationIds.forEach((notificationId) => {
          const target = newState.notifications.find(
            (notification) => notification.id === notificationId
          )
          if (target && target.read) {
            target.read = false
            newState.unreadCount += 1
          }
        })
        break
      }
      case DISMISS_NOTIFICATION:
        newState.notifications = state.notifications.filter(
          (notification) => notification.id !== action.notificationId
        )
        break
      case CLEAR_NOTIFICATIONS:
        // Idempotent filter. No carve-out for a listening manager — their
        // live Listen/Chat controls persist outside the bell until CALL_END.
        newState.notifications = state.notifications.filter(
          (notification) => !action.notificationIds.includes(notification.id)
        )
        break
      case TOGGLE_PANEL:
        newState.panelOpen = !state.panelOpen
        break
      case SET_PANEL_OPEN:
        newState.panelOpen = action.open
        break
      case SET_LOADING:
        newState.loading = action.loading
        break
      case SET_CURRENT_ORG_USERS:
        newState.currentOrgUsers = action.users
        break
      case SET_CURRENT_ORG_TAGS:
        newState.currentOrgTags = action.tags
        break
      case SET_ALERT_COVERAGE:
        newState.alertCoverage = action.alertCoverage
        break
      default:
        break
    }
  })
}
